import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Upload,
  Building2,
  User,
  Mail,
  Save,
  X,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  Copy,
  Eye,
  EyeOff,
  FileText,
} from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./LenderAccountPage.css";
import usePersistentForm from "../../../hooks/usePersistentForm";
import { useLenderNavigationGuard } from "../../../components/layout/lender/LenderNavigationGuardContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const MAX_LOGO_SIZE_MB = 12;
const MAX_DOCUMENT_SIZE_MB = 20;

const ACCEPTED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
];

const ACCEPTED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
];

const COUNTRY_LABELS = {
  BE: "Belgium",
  ES: "Spain",
};

const ROLE_LABELS = {
  LOAN_OFFICER: "Loan officer",
  CREDIT_ANALYST: "Credit analyst",
  BRANCH_MANAGER: "Branch manager",
  UNDERWRITER: "Underwriter",
  ADMIN: "Admin",
  OTHER: "Other",
};

function formatMarketRegions(regions) {
  if (!Array.isArray(regions)) {
    return "";
  }

  return regions
    .map((region) => {
      if (!region) {
        return "";
      }

      return region.name || region.code || "";
    })
    .filter(Boolean)
    .join(", ");
}

const LENDER_ACCOUNT_STORAGE_KEY = "legup_lender_account_form";

const INITIAL_FORM = {
  organisation_name: "",
  country_code: "",
  company_key: "",
  org_role: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  email: "",
  phone: "",
  vat_number: "",
  nationality: "",
  legal_document_url: "",
  legal_document_name: "",
  remove_logo: false,
};

function formatPhoneByCountry(rawValue, countryCode) {
  const value = String(rawValue || "");
  const startsWithPlus = value.trim().startsWith("+");

  let cleaned = value.replace(/[^\d+]/g, "");

  if (!cleaned) return "";

  if (countryCode === "BE") {
    const maxDigits = startsWithPlus || cleaned.startsWith("+32") ? 11 : 10;
    const digitsOnly = cleaned.replace(/\D/g, "").slice(0, maxDigits);

    if (startsWithPlus || cleaned.startsWith("+32")) {
      let localDigits = digitsOnly;

      if (localDigits.startsWith("32")) {
        localDigits = localDigits.slice(2);
      }

      localDigits = localDigits.slice(0, 9);

      return [
        "+32",
        localDigits.slice(0, 3),
        localDigits.slice(3, 5),
        localDigits.slice(5, 7),
        localDigits.slice(7, 9),
      ]
        .filter(Boolean)
        .join(" ");
    }

    return [
      digitsOnly.slice(0, 4),
      digitsOnly.slice(4, 7),
      digitsOnly.slice(7, 9),
      digitsOnly.slice(9, 10),
    ]
      .filter(Boolean)
      .join(" ");
  }

  if (countryCode === "ES") {
    const maxDigits = startsWithPlus || cleaned.startsWith("+34") ? 11 : 9;
    const digitsOnly = cleaned.replace(/\D/g, "").slice(0, maxDigits);

    if (startsWithPlus || cleaned.startsWith("+34")) {
      let localDigits = digitsOnly;

      if (localDigits.startsWith("34")) {
        localDigits = localDigits.slice(2);
      }

      localDigits = localDigits.slice(0, 9);

      return [
        "+34",
        localDigits.slice(0, 3),
        localDigits.slice(3, 6),
        localDigits.slice(6, 9),
      ]
        .filter(Boolean)
        .join(" ");
    }

    return [
      digitsOnly.slice(0, 3),
      digitsOnly.slice(3, 6),
      digitsOnly.slice(6, 9),
    ]
      .filter(Boolean)
      .join(" ");
  }

  return value;
}

function Field({ label, children, hint = "" }) {
  return (
    <div className="lender-account__field">
      <label className="lender-account__label">{label}</label>
      {children}
      {hint ? <p className="lender-account__field-hint">{hint}</p> : null}
    </div>
  );
}

function Section({ title, icon, children, delayClass = "" }) {
  return (
    <section className={`lender-account__section ${delayClass}`}>
      <div className="lender-account__section-header">
        <div className="lender-account__section-title-wrap">
          <span className="lender-account__section-icon">{icon}</span>
          <h2 className="lender-account__section-title">{title}</h2>
        </div>
      </div>
      <div className="lender-account__section-body">{children}</div>
    </section>
  );
}

function normalizeComparableForm(formData = {}) {
  return Object.keys(INITIAL_FORM).reduce((result, key) => {
    const value = formData[key];

    result[key] =
      typeof value === "boolean" ? value : String(value ?? "").trim();

    return result;
  }, {});
}

export default function LenderAccountPage() {
  const { t } = useTranslation("lender-account");
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const { registerNavigationGuard } = useLenderNavigationGuard();
  const {
    me,
    account,
    profile,
    organisation,
    refreshMe,
    setFormDirty,
    registerDiscardChangesHandler,
  } = useOutletContext();

  const marketRegions =
    profile?.market_regions ||
    me?.profile?.market_regions ||
    me?.dashboard_summary?.market_regions ||
    organisation?.market_regions ||
    [];

  const marketRegionDisplay = React.useMemo(
    () => formatMarketRegions(marketRegions),
    [marketRegions],
  );

  const logoInputRef = React.useRef(null);
  const legalDocInputRef = React.useRef(null);

  const [isSendingReset, setIsSendingReset] = React.useState(false);
  const [showCompanyKey, setShowCompanyKey] = React.useState(false);

  const [feedbackModal, setFeedbackModal] = React.useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  const [passwordResetConfirmOpen, setPasswordResetConfirmOpen] =
    React.useState(false);

  const [leaveModal, setLeaveModal] = React.useState({
    open: false,
    targetPath: null,
    navigateOptions: null,
  });

  const { form, setForm, hydrateForm, clearStoredForm, hasStoredDraft } =
    usePersistentForm(LENDER_ACCOUNT_STORAGE_KEY, INITIAL_FORM);

  const [logoFile, setLogoFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState("");

  const [legalDocumentFile, setLegalDocumentFile] = React.useState(null);

  const [isSaving, setIsSaving] = React.useState(false);
  const [companyKey, setCompanyKey] = React.useState("");
  const [removeLogo, setRemoveLogo] = React.useState(false);

  const [initialForm, setInitialForm] = React.useState(null);

  async function fetchCompanyKey() {
    try {
      const token = await getAccessTokenSilently();

      const res = await fetch(`${API_BASE}/api/lender/company-key/reveal/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.company_key) {
        throw new Error(
          data.detail ||
            t(
              "messages.companyKeyLoadError",
              "The company key could not be retrieved.",
            ),
        );
      }

      setCompanyKey(data.company_key);
      setShowCompanyKey(true);
    } catch (error) {
      console.error("Failed to reveal company key", error);

      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t(
            "messages.companyKeyLoadError",
            "The company key could not be retrieved.",
          ),
      });
    }
  }

  React.useEffect(() => {
    if (!showCompanyKey) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowCompanyKey(false);
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [showCompanyKey]);

  function closeFeedbackModal() {
    setFeedbackModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });
  }

  const didHydrateFromBackendRef = React.useRef(false);

  React.useEffect(() => {
    if (!me || didHydrateFromBackendRef.current) {
      return;
    }

    const backendForm = {
      organisation_name:
        profile?.organisation_name || account?.organisation_name || "",

      country_code: profile?.country_code || organisation?.country_code || "",

      company_key: organisation?.company_key || account?.company_key || "",

      org_role: account?.org_role || "",
      first_name: account?.first_name || "",
      last_name: account?.last_name || "",
      date_of_birth: profile?.date_of_birth || "",
      nationality: profile?.nationality || "",
      email: account?.email || "",
      phone: profile?.phone || "",
      vat_number: profile?.vat_number || "",

      legal_document_url: profile?.legal_document_url || "",

      legal_document_name: profile?.legal_document_name || "",

      ltv_threshold_pct: profile?.ltv_threshold_pct ?? "",

      dsti_threshold_pct: profile?.dsti_threshold_pct ?? "",

      living_surplus_min: profile?.living_surplus_min ?? "",

      income_stability_min_months: profile?.income_stability_min_months ?? "",

      remove_logo: false,
    };

    setInitialForm(backendForm);

    if (!hasStoredDraft) {
      hydrateForm(backendForm);
    }

    didHydrateFromBackendRef.current = true;
  }, [me, account, profile, organisation, hydrateForm, hasStoredDraft]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "phone") {
        return {
          ...prev,
          phone: formatPhoneByCountry(value, prev.country_code),
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setRemoveLogo(true);

    setForm((prev) => ({
      ...prev,
      remove_logo: true,
    }));

    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.logoTypeError",
          "Please upload a PNG, JPG, JPEG, WEBP or SVG image.",
        ),
      });
      e.target.value = "";
      return;
    }

    const maxBytes = MAX_LOGO_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.logoSizeError",
          "The logo must be smaller than 12 MB.",
        ),
      });
      e.target.value = "";
      return;
    }

    setLogoFile(file);
    setRemoveLogo(false);
    setForm((prev) => ({
      ...prev,
      remove_logo: false,
    }));
  };

  const handleLegalDocumentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.legalDocumentTypeError",
          "Please upload a PDF, PNG or JPG file.",
        ),
      });
      e.target.value = "";
      return;
    }

    const maxBytes = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.legalDocumentSizeError",
          "The document must be smaller than 20 MB.",
        ),
      });
      e.target.value = "";
      return;
    }

    setLegalDocumentFile(file);
    setForm((prev) => ({
      ...prev,
      legal_document_name: file.name,
    }));
  };

  const triggerLogoPicker = () => {
    logoInputRef.current?.click();
  };

  const triggerLegalDocumentPicker = () => {
    legalDocInputRef.current?.click();
  };

  const handleCopyCompanyKey = async () => {
    try {
      await navigator.clipboard.writeText(companyKey || "");
      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Copied"),
        text: t(
          "messages.companyKeyCopied",
          "The company key has been copied to your clipboard.",
        ),
      });
    } catch {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.companyKeyCopyError",
          "Could not copy the company key.",
        ),
      });
    }
  };

  const hasChanges = React.useMemo(() => {
    if (!initialForm) {
      return false;
    }

    const formChanged =
      JSON.stringify(normalizeComparableForm(form)) !==
      JSON.stringify(normalizeComparableForm(initialForm));

    const hasNewLogo = logoFile instanceof File;
    const hasNewLegalDocument = legalDocumentFile instanceof File;

    return formChanged || hasNewLogo || hasNewLegalDocument || removeLogo;
  }, [form, initialForm, logoFile, legalDocumentFile, removeLogo]);

  React.useEffect(() => {
    setFormDirty?.(hasChanges);
  }, [hasChanges, setFormDirty]);

  React.useEffect(() => {
    return () => {
      setFormDirty?.(false);
    };
  }, [setFormDirty]);

  const discardAccountDraft = React.useCallback(() => {
    clearStoredForm();

    setLogoFile(null);
    setLogoPreview("");
    setLegalDocumentFile(null);
    setRemoveLogo(false);

    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }

    if (legalDocInputRef.current) {
      legalDocInputRef.current.value = "";
    }
  }, [clearStoredForm]);

  React.useEffect(() => {
    if (!registerDiscardChangesHandler) {
      return undefined;
    }

    return registerDiscardChangesHandler(discardAccountDraft);
  }, [registerDiscardChangesHandler, discardAccountDraft]);

  /*
   * Register the account page with the shared lender navigation guard.
   *
   * Any lender navigation that goes through requestNavigation(...)
   * (sidebar, notifications, dashboard shortcuts, etc.) will call this
   * guard before changing route.
   */
  const handleGuardedNavigation = React.useCallback(
    ({ targetPath, navigateOptions = {} }) => {
      if (!hasChanges) {
        return false;
      }

      setLeaveModal({
        open: true,
        targetPath,
        navigateOptions,
      });

      /*
       * Returning true tells LenderNavigationGuardProvider
       * that navigation has been intercepted.
       */
      return true;
    },
    [hasChanges],
  );

  React.useEffect(() => {
    return registerNavigationGuard(handleGuardedNavigation);
  }, [handleGuardedNavigation, registerNavigationGuard]);

  /*
   * Protect browser refresh, closing the tab/window and navigating
   * away from the application itself.
   *
   * Browsers intentionally show their own generic warning text here.
   */
  React.useEffect(() => {
    function handleBeforeUnload(event) {
      if (!hasChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  const closeLeaveModal = React.useCallback(() => {
    setLeaveModal({
      open: false,
      targetPath: null,
      navigateOptions: null,
    });
  }, []);

  const handleStayOnPage = React.useCallback(() => {
    closeLeaveModal();
  }, [closeLeaveModal]);

  const handleLeavePage = React.useCallback(() => {
    const targetPath = leaveModal.targetPath;
    const navigateOptions = leaveModal.navigateOptions || {};

    /*
     * "Leave" means discard the unsaved local draft.
     * The persisted backend values remain unchanged.
     */
    discardAccountDraft();
    setFormDirty?.(false);

    closeLeaveModal();

    if (targetPath) {
      /*
       * Use raw navigate() here, not requestNavigation().
       * The user has already explicitly confirmed leaving.
       */
      navigate(targetPath, navigateOptions);
    }
  }, [
    closeLeaveModal,
    discardAccountDraft,
    leaveModal.navigateOptions,
    leaveModal.targetPath,
    navigate,
    setFormDirty,
  ]);

  React.useEffect(() => {
    if (!leaveModal.open) {
      return undefined;
    }

    function handleLeaveModalKeyDown(event) {
      if (event.key === "Escape") {
        handleStayOnPage();
      }
    }

    document.addEventListener("keydown", handleLeaveModalKeyDown);

    return () => {
      document.removeEventListener("keydown", handleLeaveModalKeyDown);
    };
  }, [leaveModal.open, handleStayOnPage]);

  const handleSave = async (e) => {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.validationTitle", "Check your information"),
        text: validationError,
      });

      return;
    }

    try {
      setIsSaving(true);

      const token = await getAccessTokenSilently();

      const payload = new FormData();
      payload.append("organisation_name", form.organisation_name);
      payload.append("country_code", form.country_code);
      payload.append("org_role", form.org_role);
      payload.append("first_name", form.first_name);
      payload.append("last_name", form.last_name);
      payload.append("date_of_birth", form.date_of_birth);
      payload.append("nationality", form.nationality);
      payload.append("email", form.email);
      payload.append("phone", form.phone);
      payload.append("vat_number", form.vat_number);
      payload.append("ltv_threshold_pct", form.ltv_threshold_pct);
      payload.append("dsti_threshold_pct", form.dsti_threshold_pct);
      payload.append("living_surplus_min", form.living_surplus_min);
      payload.append(
        "income_stability_min_months",
        form.income_stability_min_months,
      );
      payload.append("remove_logo", removeLogo ? "true" : "false");

      if (logoFile) {
        payload.append("logo", logoFile);
      }

      if (legalDocumentFile) {
        payload.append("legal_document", legalDocumentFile);
      }

      const res = await fetch(`${API_BASE}/api/lender/account/update/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setFeedbackModal({
          open: true,
          type: "error",
          title: t("messages.errorTitle", "Something went wrong"),
          text: t(
            "messages.saveError",
            "We could not save your changes at the moment.",
          ),
        });
        return;
      }

      scrollToTopAfterSuccessfulRequest(res);

      const nextForm = {
        organisation_name:
          data?.profile?.organisation_name || form.organisation_name || "",

        country_code: data?.profile?.country_code || form.country_code || "",

        company_key: form.company_key || "",

        org_role: account?.org_role || form.org_role || "",

        first_name: data?.account?.first_name || form.first_name || "",

        last_name: data?.account?.last_name || form.last_name || "",

        date_of_birth: data?.profile?.date_of_birth || form.date_of_birth || "",

        nationality: data?.profile?.nationality || form.nationality || "",

        email: data?.account?.email || form.email || "",

        phone: data?.profile?.phone || form.phone || "",

        vat_number: data?.profile?.vat_number || form.vat_number || "",

        legal_document_url:
          data?.profile?.legal_document_url || form.legal_document_url || "",

        legal_document_name:
          data?.profile?.legal_document_name || form.legal_document_name || "",
        remove_logo: false,
      };

      hydrateForm(nextForm);

      setInitialForm(nextForm);
      clearStoredForm();
      setFormDirty?.(false);

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.updateSuccessTitle", "Updated successfully"),
        text: t(
          "messages.saveSuccess",
          "Your account details were updated successfully.",
        ),
      });
      setLogoFile(null);
      setLegalDocumentFile(null);
      setRemoveLogo(false);

      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }

      if (legalDocInputRef.current) {
        legalDocInputRef.current.value = "";
      }

      await refreshMe?.({ silent: true });
    } catch (error) {
      console.error("Failed to save lender account", error);
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text: t(
          "messages.saveError",
          "We could not save your changes at the moment.",
        ),
      });
    } finally {
      setIsSaving(false);
    }
  };

  async function handlePasswordReset() {
    try {
      setIsSendingReset(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}/api/auth/password-reset/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t(
              "messages.passwordResetError",
              "Could not send the password reset email.",
            ),
        );
      }

      scrollToTopAfterSuccessfulRequest(response);

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle", "Email sent"),
        text: t(
          "messages.passwordResetSent",
          "A password reset email has been sent to your account email address.",
        ),
      });
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle", "Something went wrong"),
        text:
          error.message ||
          t(
            "messages.passwordResetError",
            "Could not send the password reset email.",
          ),
      });
    } finally {
      setIsSendingReset(false);
    }
  }

  function openPasswordResetConfirmation() {
    if (isSendingReset) return;

    setPasswordResetConfirmOpen(true);
  }

  function closePasswordResetConfirmation() {
    if (isSendingReset) return;

    setPasswordResetConfirmOpen(false);
  }

  async function confirmPasswordReset() {
    setPasswordResetConfirmOpen(false);
    await handlePasswordReset();
  }

  function scrollToTopAfterSuccessfulRequest(response) {
    if (response.status !== 200) {
      return;
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  }

  const savedLogoUrl =
    account?.image_url || profile?.logo_url || profile?.image_url || "";

  const resolvedLogoUrl =
    savedLogoUrl && savedLogoUrl.startsWith("http")
      ? savedLogoUrl
      : savedLogoUrl
        ? `${API_BASE}${savedLogoUrl}`
        : "";

  const visibleLogoUrl = logoPreview || resolvedLogoUrl;

  React.useEffect(() => {
    if (!(logoFile instanceof File)) {
      setLogoPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(logoFile);
    setLogoPreview(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [logoFile]);

  function handleDiscardChanges() {
    if (!initialForm) return;

    hydrateForm(initialForm);
    clearStoredForm();

    setLogoFile(null);
    setLogoPreview("");
    setLegalDocumentFile(null);
    setRemoveLogo(false);

    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }

    if (legalDocInputRef.current) {
      legalDocInputRef.current.value = "";
    }

    setFormDirty?.(false);
  }

  function validateForm() {
    if (!form.organisation_name.trim()) {
      return t(
        "validation.organisationRequired",
        "Organisation name is required.",
      );
    }

    if (!form.first_name.trim() || !form.last_name.trim()) {
      return t(
        "validation.nameRequired",
        "First name and last name are required.",
      );
    }

    if (form.date_of_birth) {
      const selectedDate = new Date(`${form.date_of_birth}T00:00:00`);
      const today = new Date();

      if (selectedDate > today) {
        return t(
          "validation.futureBirthDate",
          "Date of birth cannot be in the future.",
        );
      }
    }

    return "";
  }

  React.useEffect(() => {
    if (!passwordResetConfirmOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape" && !isSendingReset) {
        setPasswordResetConfirmOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [passwordResetConfirmOpen, isSendingReset]);

  return (
    <div className="lender-account-page">
      <div className="lender-account-page__hero lender-account__fade-1">
        <h1 className="lender-account-page__title">{t("title")}</h1>
        <p className="lender-account-page__subtitle">{t("subtitle")}</p>
      </div>
      {hasStoredDraft && hasChanges ? (
        <div className="lender-account__draft-notice">
          <div>
            <strong>
              {t("draft.restoredTitle", "Unsaved draft restored")}
            </strong>

            <p>
              {t(
                "draft.restoredText",
                "Your unsaved account changes from the previous session have been restored.",
              )}
            </p>
          </div>

          <button type="button" onClick={handleDiscardChanges}>
            {t("draft.discard", "Discard draft")}
          </button>
        </div>
      ) : null}
      <form
        className="lender-account lender-account__fade-2"
        onSubmit={handleSave}
      >
        <Section
          title={t("sections.organisation")}
          icon={<Building2 size={18} />}
          delayClass="lender-account__fade-2"
        >
          <div className="lender-account__logo-block">
            <div className="lender-account__logo-preview">
              {visibleLogoUrl && !removeLogo ? (
                <img
                  src={visibleLogoUrl}
                  alt={t("logo.alt", "Organisation logo")}
                  className="lender-account__logo-image"
                  onError={(e) => {
                    console.error("Logo failed to load:", visibleLogoUrl);
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="lender-account__logo-placeholder">
                  <Building2 size={28} />
                </div>
              )}
            </div>

            <div className="lender-account__logo-actions">
              <button
                type="button"
                className="lender-account__upload-btn"
                onClick={triggerLogoPicker}
              >
                <Upload size={16} />
                {t("logo.upload")}
              </button>

              {visibleLogoUrl && !removeLogo ? (
                <button
                  type="button"
                  className="lender-account__upload-btn lender-account__upload-btn--ghost"
                  onClick={handleRemoveLogo}
                >
                  <X size={16} />
                  {t("logo.remove", "Remove logo")}
                </button>
              ) : null}

              <input
                ref={logoInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.svg"
                hidden
                onChange={handleLogoChange}
              />

              <p className="lender-account__hint">{t("logo.hint")}</p>
            </div>
          </div>

          <div className="lender-account__grid lender-account__grid--2">
            <Field label={t("fields.organisationName")}>
              <input
                name="organisation_name"
                value={form.organisation_name}
                onChange={handleChange}
                className="lender-account__input"
              />
            </Field>

            <Field label={t("fields.companyKey")}>
              {!companyKey ? (
                <button
                  type="button"
                  className="lender-account__upload-btn"
                  onClick={fetchCompanyKey}
                >
                  <KeyRound size={16} />
                  {t("actions.generateKey", "Reveal company key")}
                </button>
              ) : (
                <div className="lender-account__secure-inline">
                  <input
                    value={showCompanyKey ? companyKey : "••••••••••"}
                    className="lender-account__input"
                    disabled
                  />
                  <button
                    type="button"
                    className="lender-account__icon-btn"
                    onClick={() => setShowCompanyKey((previous) => !previous)}
                    aria-label={
                      showCompanyKey
                        ? t("actions.hideCompanyKey", "Hide company key")
                        : t("actions.showCompanyKey", "Show company key")
                    }
                    title={
                      showCompanyKey
                        ? t("actions.hideCompanyKey", "Hide company key")
                        : t("actions.showCompanyKey", "Show company key")
                    }
                  >
                    {showCompanyKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>

                  <button
                    type="button"
                    className="lender-account__icon-btn"
                    onClick={handleCopyCompanyKey}
                    aria-label={t("actions.copyCompanyKey", "Copy company key")}
                    title={t("actions.copyCompanyKey", "Copy company key")}
                  >
                    <Copy size={16} />
                  </button>
                </div>
              )}
            </Field>

            <Field label={t("fields.country")}>
              <input
                value={COUNTRY_LABELS[form.country_code] || ""}
                disabled
                className="lender-account__input"
              />
            </Field>

            <Field label={t("fields.marketRegions", "Market regions")}>
              <input
                value={marketRegionDisplay}
                disabled
                className="lender-account__input"
              />
            </Field>

            <Field label={t("fields.roleInOrganisation")}>
              <input
                value={ROLE_LABELS[form.org_role] || form.org_role || ""}
                disabled
                className="lender-account__input"
              />
            </Field>

            <Field
              label={t("fields.vatNumber")}
              hint={
                form.country_code === "BE"
                  ? "Format: BE0123456789"
                  : "Format: ESB12345678"
              }
            >
              <input
                name="vat_number"
                value={form.vat_number}
                onChange={handleChange}
                className="lender-account__input"
              />
            </Field>

            <Field label={t("fields.notaryDocument")}>
              <div className="lender-account__document-upload">
                {form.legal_document_url ? (
                  <a
                    href={`${API_BASE}${form.legal_document_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="lender-account__document-link"
                  >
                    <FileText size={16} />
                    {t("actions.viewDocument", "View current document")}
                  </a>
                ) : null}

                <button
                  type="button"
                  className="lender-account__upload-btn"
                  onClick={triggerLegalDocumentPicker}
                >
                  <Upload size={16} />
                  {t("actions.replaceDocument", "Upload / replace")}
                </button>

                <input
                  ref={legalDocInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  hidden
                  onChange={handleLegalDocumentChange}
                />

                {legalDocumentFile ? (
                  <div className="lender-account__selected-document">
                    <FileText size={15} />

                    <span>{legalDocumentFile.name}</span>

                    <button
                      type="button"
                      onClick={() => {
                        setLegalDocumentFile(null);

                        setForm((previous) => ({
                          ...previous,
                          legal_document_name:
                            initialForm?.legal_document_name || "",
                        }));

                        if (legalDocInputRef.current) {
                          legalDocInputRef.current.value = "";
                        }
                      }}
                      aria-label={t(
                        "actions.removeSelectedDocument",
                        "Remove selected document",
                      )}
                    >
                      <X size={15} />
                    </button>
                  </div>
                ) : null}
              </div>
            </Field>
          </div>
        </Section>

        <Section
          title={t("sections.personalInformation")}
          icon={<User size={18} />}
          delayClass="lender-account__fade-3"
        >
          <div className="lender-account__grid lender-account__grid--2">
            <Field label={t("fields.firstName")}>
              <input
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                className="lender-account__input"
              />
            </Field>

            <Field label={t("fields.lastName")}>
              <input
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                className="lender-account__input"
              />
            </Field>

            <Field label={t("fields.dateOfBirth")}>
              <input
                type="date"
                name="date_of_birth"
                value={form.date_of_birth}
                onChange={handleChange}
                className="lender-account__input"
              />
            </Field>
          </div>
        </Section>

        <Section
          title={t("sections.contactDetails")}
          icon={<Mail size={18} />}
          delayClass="lender-account__fade-4"
        >
          <div className="lender-account__grid lender-account__grid--2">
            <Field label={t("fields.emailAddress")}>
              <input
                type="email"
                name="email"
                value={form.email}
                className="lender-account__input"
                disabled
              />
            </Field>

            <Field
              label={t("fields.phoneNumber")}
              hint={
                form.country_code === "BE"
                  ? t(
                      "fields.phoneHintBE",
                      "Belgian format will be applied automatically.",
                    )
                  : t(
                      "fields.phoneHintES",
                      "Spanish format will be applied automatically.",
                    )
              }
            >
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="lender-account__input"
                placeholder={
                  form.country_code === "BE"
                    ? "+32 470 12 34 56"
                    : form.country_code === "ES"
                      ? "+34 612 345 678"
                      : t("fields.phonePlaceholder", "Enter phone number")
                }
                maxLength={
                  form.country_code === "BE"
                    ? 16
                    : form.country_code === "ES"
                      ? 14
                      : 20
                }
              />
            </Field>
          </div>
        </Section>

        <Section
          title={t("sections.security")}
          icon={<KeyRound size={18} />}
          delayClass="lender-account__fade-5"
        >
          <div className="lender-account__security">
            <div className="lender-account__security-info">
              <div className="lender-account__security-icon">
                <KeyRound size={18} />
              </div>

              <div>
                <h3 className="lender-account__security-title">
                  {t("security.passwordTitle")}
                </h3>
                <p className="lender-account__security-text">
                  {t("security.passwordText")}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="lender-account__reset-btn"
              onClick={openPasswordResetConfirmation}
              disabled={isSendingReset}
            >
              {isSendingReset
                ? t("buttons.sending", "Sending...")
                : t("buttons.sendPasswordReset", "Send password reset email")}
            </button>
          </div>
        </Section>

        <div className="lender-account__actions lender-account__fade-6">
          <button
            type="button"
            className="lender-account__discard-btn"
            disabled={isSaving || !hasChanges}
            onClick={handleDiscardChanges}
          >
            {t("actions.discardChanges", "Discard changes")}
          </button>

          <button
            type="submit"
            className="lender-account__save-btn"
            disabled={isSaving || !hasChanges}
          >
            <Save size={16} />

            {isSaving ? t("actions.saving") : t("actions.saveChanges")}
          </button>
        </div>
      </form>

      {feedbackModal.open ? (
        <div
          className="lender-account-modal-overlay"
          onClick={closeFeedbackModal}
        >
          <div
            className={`lender-account-modal ${
              feedbackModal.type === "error"
                ? "lender-account-modal--error"
                : "lender-account-modal--success"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lender-account-modal__icon">
              {feedbackModal.type === "error" ? (
                <AlertCircle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>

            <div className="lender-account-modal__content">
              <h3>{feedbackModal.title}</h3>
              <p>{feedbackModal.text}</p>
            </div>

            <button
              type="button"
              className="lender-account-modal__close"
              onClick={closeFeedbackModal}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {leaveModal.open ? (
        <div
          className="lender-account-confirm-overlay"
          onMouseDown={handleStayOnPage}
        >
          <div
            className="lender-account-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="lender-account-leave-title"
            aria-describedby="lender-account-leave-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="lender-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="lender-account-confirm__content">
              <h3 id="lender-account-leave-title">
                {t("confirm.leaveTitle", "Leave without saving?")}
              </h3>

              <p id="lender-account-leave-description">
                {t(
                  "confirm.leaveText",
                  "You have unsaved changes. If you leave this page now, your changes will be lost.",
                )}
              </p>

              <div className="lender-account-confirm__actions">
                <button
                  type="button"
                  className="lender-account-confirm__cancel"
                  onClick={handleStayOnPage}
                >
                  {t("confirm.stay", "Stay")}
                </button>

                <button
                  type="button"
                  className="lender-account-confirm__confirm"
                  onClick={handleLeavePage}
                >
                  {t("confirm.leave", "Leave")}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="lender-account-confirm__close"
              onClick={handleStayOnPage}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {passwordResetConfirmOpen ? (
        <div
          className="lender-account-confirm-overlay"
          onMouseDown={closePasswordResetConfirmation}
        >
          <div
            className="lender-account-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="lender-password-reset-title"
            aria-describedby="lender-password-reset-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="lender-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="lender-account-confirm__content">
              <h3 id="lender-password-reset-title">
                {t("confirm.passwordResetTitle", "Send password reset email?")}
              </h3>

              <p id="lender-password-reset-description">
                {t(
                  "confirm.passwordResetText",
                  "A password reset link will be sent to your registered email address.",
                )}
              </p>

              {form.email ? (
                <div className="lender-account-confirm__email">
                  <Mail size={15} />
                  <span>{form.email}</span>
                </div>
              ) : null}

              <div className="lender-account-confirm__actions">
                <button
                  type="button"
                  className="lender-account-confirm__cancel"
                  onClick={closePasswordResetConfirmation}
                  disabled={isSendingReset}
                >
                  {t("confirm.cancel", "Cancel")}
                </button>

                <button
                  type="button"
                  className="lender-account-confirm__confirm"
                  onClick={confirmPasswordReset}
                  disabled={isSendingReset}
                >
                  {isSendingReset
                    ? t("buttons.sending", "Sending...")
                    : t("confirm.sendEmail", "Send email")}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="lender-account-confirm__close"
              onClick={closePasswordResetConfirmation}
              disabled={isSendingReset}
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
